import React from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Helmet } from "../components/Head";
import { BLOG_POSTS, findPost } from "../content/blogPosts";
import { AUTHOR, GALIKIN_URL, SITE_URL, authorSchema, publisherSchema } from "../content/site";
import NotFoundPage from "./NotFoundPage";
import "./style/content.css";

const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("cs-CZ", { day: "numeric", month: "long", year: "numeric" });

// interní odkazy v Markdownu přes router, externí do nového okna
const MdLink = ({ href = "", children }) =>
  href.startsWith("/") ? (
    <Link to={href}>{children}</Link>
  ) : (
    <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
  );

export function AuthorBox() {
  return (
    <div className="content-author">
      <div>
        <strong>{AUTHOR.name}</strong> – {AUTHOR.role}, {AUTHOR.past}. Autor metodiky a statistik FinPilot.{" "}
        <Link to="/o-projektu">O projektu a metodice</Link>
      </div>
    </div>
  );
}

export function BlogListPage() {
  const url = `${SITE_URL}/blog`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "FinPilot blog – čísla o českých firmách",
    url,
    inLanguage: "cs-CZ",
    publisher: publisherSchema,
    blogPost: BLOG_POSTS.map((p) => ({ "@type": "BlogPosting", headline: p.title, url: `${url}/${p.slug}`, datePublished: p.date })),
  };
  return (
    <main className="content-page content-page--wide">
      <Helmet>
        <title>Blog: čísla o českých firmách a oborech | FinPilot</title>
        <meta name="description" content="Analýzy z dat o více než 25 000 českých firmách: růst oborů, ztrátovost, marže podle velikosti firmy, splatnost faktur a ocenění firem." />
        <meta name="robots" content="index,follow" />
        <meta property="og:title" content="Blog: čísla o českých firmách a oborech | FinPilot" />
        <meta property="og:url" content={url} />
        <link rel="canonical" href={url} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <span className="content-kicker">Blog</span>
      <h1>Čísla o českých firmách a oborech</h1>
      <p>
        Krátké analýzy z vlastní statistiky FinPilot: jak rostou jednotlivé obory, kolik firem je ve ztrátě, jak dlouho se čeká
        na zaplacení faktur a jak se liší malé a velké firmy. Každý článek uvádí období, počet firem a způsob výpočtu.
      </p>
      <div className="content-cards">
        {BLOG_POSTS.map((p) => (
          <Link key={p.slug} to={`/blog/${p.slug}`} className="content-card">
            <time dateTime={p.date}>{fmtDate(p.date)}</time>
            <h2>{p.title}</h2>
            <p>{p.description}</p>
          </Link>
        ))}
      </div>
      <p>
        Řešíte prodej firmy nebo nástupnictví? Tomu se věnuje poradenská společnost Galik In –{" "}
        <a href={`${GALIKIN_URL}/radce`} target="_blank" rel="noopener noreferrer">rádce pro majitele firem</a>.
      </p>
    </main>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const post = findPost(slug);
  if (!post) return <NotFoundPage />;
  const url = `${SITE_URL}/blog/${post.slug}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    url,
    mainEntityOfPage: url,
    inLanguage: "cs-CZ",
    datePublished: post.date,
    dateModified: post.updated || post.date,
    author: authorSchema,
    publisher: publisherSchema,
    keywords: post.tags,
  };
  const others = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);
  return (
    <main className="content-page">
      <Helmet>
        <title>{`${post.title} | FinPilot`}</title>
        <meta name="description" content={post.description} />
        <meta name="robots" content="index,follow" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:url" content={url} />
        <link rel="canonical" href={url} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <Link to="/blog" className="content-kicker">← Blog</Link>
      <article>
        <h1>{post.title}</h1>
        <div className="content-meta">
          {AUTHOR.name} · <time dateTime={post.date}>{fmtDate(post.date)}</time>
          {post.updated && post.updated !== post.date ? ` · aktualizováno ${fmtDate(post.updated)}` : ""}
        </div>
        <div className="content-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: MdLink }}>{post.body}</ReactMarkdown>
        </div>
      </article>
      <p className="content-meta">
        Zdroj dat: účetní závěrky českých firem zveřejněné ve Sbírce listin, vlastní zpracování FinPilot.{" "}
        <Link to="/o-projektu">Metodika</Link>
      </p>
      <AuthorBox />
      <h2>Další články</h2>
      <div className="content-cards">
        {others.map((p) => (
          <Link key={p.slug} to={`/blog/${p.slug}`} className="content-card">
            <h3>{p.title}</h3>
            <p>{p.description}</p>
          </Link>
        ))}
      </div>
    </main>
  );
}
